import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateVersionRequestDto, FlowVersionDetailDto } from '../types/FlowVersion';
import { FlowVersionService } from '../service/FlowVersionService';

const FIVE_MINUTES_MS = 5 * 60 * 1000;

export const useDeployedVersion = (flowId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['flow-version-deployed', flowId],
    queryFn: async () => {
      try {
        const response = await FlowVersionService.getDeployedVersion(flowId);
        return (response as { data: FlowVersionDetailDto }).data;
      } catch (error: any) {
        if (error?.statusCode === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!flowId && enabled,
    retry: false,
    staleTime: FIVE_MINUTES_MS,
  });
};

export const useVersions = (
  flowId: string,
  page: number = 1,
  pageSize: number = 50,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ['flow-versions', flowId, page, pageSize],
    queryFn: async () => {
      const response = await FlowVersionService.getVersions(flowId, page, pageSize);
      return response;
    },
    enabled: !!flowId && enabled,
    staleTime: FIVE_MINUTES_MS,
  });
};

export const useVersionHistory = (
  flowId: string,
  versionId: string,
  page: number = 1,
  pageSize: number = 10,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ['flow-version-history', flowId, versionId, page, pageSize],
    queryFn: async () => {
      const response = await FlowVersionService.getVersionHistory(
        flowId,
        versionId,
        page,
        pageSize,
      );
      return response;
    },
    enabled: !!flowId && !!versionId && enabled,
  });
};

export const useCreateVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      flowId,
      data,
    }: {
      flowId: string;
      data: CreateVersionRequestDto;
    }) => {
      return await FlowVersionService.createVersion(flowId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['flow-versions'],
      });
    },
  });
};

export const usePushToProduction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      flowId,
      versionId,
    }: {
      flowId: string;
      versionId: string;
    }) => {
      return await FlowVersionService.pushToProduction(flowId, versionId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['flow-version-deployed', variables.flowId],
      });
      queryClient.invalidateQueries({
        queryKey: ['flow-versions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['flow-version-history'],
      });
    },
  });
};

export const useRollback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      flowId,
      versionId,
      historyId,
    }: {
      flowId: string;
      versionId: string;
      historyId: string;
    }) => {
      return await FlowVersionService.rollback(flowId, versionId, historyId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['flow-versions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['flow-version-history'],
      });
    },
  });
};
